import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { CircleHelp, Copy, Check, X, Sparkles } from 'lucide-react';
import { useSessionStore } from '@renderer/store/useSessionStore';
import {
  asistenteDisponible,
  preguntarAlAsistente,
  type RespuestaAyuda,
} from '@renderer/lib/asistenteAyuda';

/**
 * Sprint 134/135 — "No supe cómo hacerle".
 *
 * Las últimas seis mejoras del producto salieron de mensajes de WhatsApp de la dueña: *"¿cómo
 * vendo medio pollo?"*, *"para agregar 125 gramos de costilla"*, *"en cocina sólo aparece el
 * folio"*, *"me equivoqué al ingresar las cantidades"*, *"esa no supe cómo hacerle"*. Cada una
 * destapó un defecto que nadie había reportado — y cada una esperó horas a que alguien contestara.
 *
 * ## El botón hace dos cosas, en este orden
 *
 * 1. **Contesta**, si hay ayudante configurado, con la documentación del producto delante y
 *    prohibido inventar botones.
 * 2. **Deja constancia**, siempre. La pregunta se guarda y se copia lista para WhatsApp, aunque
 *    el ayudante haya contestado: lo que la gente no encuentra es el dato que ha valido seis
 *    sprints esta semana, y no puede depender de que alguien se acuerde de contarlo.
 *
 * En el escritorio, o si no hay `ANTHROPIC_API_KEY`, el paso 1 no existe y el botón se comporta
 * como el buzón del Sprint 134. No se rompe nada por no tener asistente.
 */

/** Nombre de la pantalla como lo diría ella, no como está en la URL. */
const PANTALLAS: Record<string, string> = {
  '/pos': 'tomando un pedido',
  '/pedidos': 'la bandeja de pedidos',
  '/productos': 'el catálogo de productos',
  '/inventario': 'existencias',
  '/promos': 'promociones',
  '/paquetes': 'paquetes',
  '/corte-caja': 'corte de caja',
  '/cierre-dia': 'cierre del día',
  '/reportes': 'reportes',
  '/estadisticas': 'cómo va el negocio',
  '/configuracion': 'configuración',
  '/domicilios': 'pedidos a domicilio',
  '/mostrador': 'venta de mostrador',
  '/dashboard': 'la pantalla de inicio',
};

function dondeEstoy(ruta: string): string {
  const clave = Object.keys(PANTALLAS).find((k) => ruta.startsWith(k));
  return clave ? PANTALLAS[clave] : ruta;
}

/** La ruta canónica que entiende el servidor ("/inventario"), no la del navegador. */
function rutaBase(ruta: string): string {
  return Object.keys(PANTALLAS).find((k) => ruta.startsWith(k)) ?? ruta;
}

export function NoPudeHacerAlgo({ version }: { version?: string }) {
  const [abierto, setAbierto] = useState(false);
  const [texto, setTexto] = useState('');
  const [copiado, setCopiado] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [hayAsistente, setHayAsistente] = useState(false);
  const [respuesta, setRespuesta] = useState<RespuestaAyuda | null>(null);
  const sesion = useSessionStore((s) => s.sesion);
  const location = useLocation();

  // Se pregunta una vez: si no hay ayudante, el botón sigue sirviendo como buzón.
  useEffect(() => {
    void asistenteDisponible().then(setHayAsistente);
  }, []);

  const pantalla = dondeEstoy(location.pathname);

  const mensaje =
    `Necesito ayuda con el sistema.\n\n` +
    `${texto.trim()}\n\n` +
    `— Estaba en: ${pantalla}\n` +
    `— Usuario: ${sesion?.nombre ?? '—'}\n` +
    (version ? `— Versión: ${version}\n` : '');

  const enviar = async () => {
    if (texto.trim().length < 5) return;
    setEnviando(true);
    setRespuesta(null);

    /*
      Primero se guarda: es lo que no se puede perder. Si el ayudante falla, si el portapapeles
      falla o si cierra la ventana sin leer nada, la pregunta sigue existiendo.
    */
    await window.api.error.reportar(
      `AYUDA: ${texto.trim()}`,
      `pantalla=${pantalla} usuario=${sesion?.nombre ?? '—'}`,
    );

    if (hayAsistente) {
      setRespuesta(await preguntarAlAsistente(texto.trim(), rutaBase(location.pathname)));
    }

    try {
      await navigator.clipboard.writeText(mensaje);
      setCopiado(true);
    } catch {
      // Sin portapapeles el texto sigue a la vista para seleccionarlo a mano. No se le dice que
      // falló algo que no le importa.
      setCopiado(false);
    }
    setEnviando(false);
  };

  const cerrar = () => {
    setAbierto(false);
    setTexto('');
    setCopiado(false);
    setRespuesta(null);
  };

  const contestado = respuesta?.ok && respuesta.respuesta && !respuesta.noSupo;
  const terminado = copiado || respuesta !== null;

  return (
    <>
      <button
        onClick={() => setAbierto(true)}
        className="btn-ghost min-h-[40px] border border-slate-300 text-sm"
        title={hayAsistente ? 'Pregúntale al ayudante' : 'Cuéntanos qué no pudiste hacer'}
      >
        <CircleHelp className="h-4 w-4" />
        {hayAsistente ? 'Ayúdame' : 'No pude hacer algo'}
      </button>

      {abierto && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="max-h-[90vh] w-full max-w-lg overflow-auto rounded-xl bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
              <h3 className="text-lg font-bold">
                {hayAsistente ? '¿Qué necesitas hacer?' : '¿Qué no pudiste hacer?'}
              </h3>
              <button onClick={cerrar} className="btn-ghost p-2" aria-label="Cerrar">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4 px-5 py-4">
              {!terminado ? (
                <>
                  <p className="text-sm text-slate-700">
                    Cuéntalo como se lo contarías a alguien por teléfono. No hace falta que sepas
                    cómo se llama en el sistema.
                  </p>
                  <textarea
                    className="input"
                    rows={4}
                    autoFocus
                    maxLength={500}
                    value={texto}
                    onChange={(e) => setTexto(e.target.value)}
                    placeholder="Ejemplo: quiero poner dos salsas por $50 aunque sean distintas, y no encontré dónde"
                  />
                  <p className="text-xs text-slate-600">
                    Estás en {pantalla}. No se manda ningún dato de tus clientes.
                  </p>
                </>
              ) : (
                <div className="space-y-3">
                  {contestado && (
                    <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
                      <div className="mb-1 flex items-center gap-2 text-sm font-semibold text-blue-900">
                        <Sparkles className="h-4 w-4" />
                        Esto es lo que tienes que hacer
                      </div>
                      <div className="whitespace-pre-wrap text-sm text-blue-950">
                        {respuesta?.respuesta}
                      </div>
                    </div>
                  )}

                  {respuesta && !contestado && (
                    /*
                      Ni una respuesta inventada ni un error técnico: si no supo, se dice y se
                      escala. Es lo que evita mandar a alguien a buscar un botón que no existe.
                    */
                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                      {respuesta.respuesta ?? respuesta.mensaje ?? 'No pude consultar ahora.'}
                    </div>
                  )}

                  <div className="flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-3">
                    <Check className="mt-0.5 h-5 w-5 shrink-0 text-emerald-700" />
                    <div className="text-sm text-emerald-900">
                      <b>Tu pregunta quedó guardada.</b>
                      <div className="mt-0.5">
                        {copiado
                          ? 'Y la copiamos por si quieres pegársela a José en WhatsApp.'
                          : 'Puedes copiar el texto de abajo y pegárselo a José en WhatsApp.'}
                      </div>
                    </div>
                  </div>

                  <pre className="max-h-32 overflow-auto whitespace-pre-wrap rounded-lg bg-slate-50 p-3 text-xs text-slate-700">
                    {mensaje}
                  </pre>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 border-t border-slate-200 px-5 py-3">
              <button onClick={cerrar} className="btn-ghost">
                {terminado ? 'Cerrar' : 'Cancelar'}
              </button>
              {!terminado && (
                <button
                  onClick={enviar}
                  className="btn-primary min-h-[44px]"
                  disabled={enviando || texto.trim().length < 5}
                >
                  {hayAsistente ? <Sparkles className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  {enviando ? 'Consultando…' : hayAsistente ? 'Preguntar' : 'Guardar y copiar'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
